import React, { useEffect, useImperativeHandle, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { ApiResponse } from 'components/services/interfaces';

import {
  getDefaultPlanningAlgorithmForm,
  PlanningAlgorithmCardSchema,
  PlanningAlgorithmFormValues
} from '../constants/schemas/PlanningAlgorithmCardSchema';
import { PlanningAlgorithm } from '../types/interfaces';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

interface RefInterface {
  handleCancel: () => void;
}

const SAME_NAME_FIELD_MESSAGE =
  'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название.';

export interface PlanningAlgorithmCardProps {
  algorithmProps: PlanningAlgorithmFormValues;
  existingAlgorithms?: PlanningAlgorithm[];
  onAdd?: (
    data: PlanningAlgorithmFormValues
  ) => Promise<ApiResponse<PlanningAlgorithm> | undefined>;
  onUpdate?: (
    id: string | number,
    data: PlanningAlgorithmFormValues
  ) => Promise<ApiResponse<PlanningAlgorithm> | undefined>;
  onDelete?: (id: string | number) => Promise<ApiResponse<null> | undefined>;
  closeDialog?: () => void;
}

const getPlanningAlgorithmFormValues = (
  algorithm?: Partial<PlanningAlgorithmFormValues>
): PlanningAlgorithmFormValues => ({
  ...getDefaultPlanningAlgorithmForm(),
  ...(algorithm ?? {})
});

const PlanningAlgorithmCard = React.forwardRef<
  RefInterface,
  PlanningAlgorithmCardProps
>(
  (
    {
      algorithmProps,
      existingAlgorithms,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog
    },
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      setError,
      clearErrors,
      formState: { isDirty, errors }
    } = useForm<PlanningAlgorithmFormValues>({
      resolver: zodResolver(PlanningAlgorithmCardSchema),
      defaultValues: getPlanningAlgorithmFormValues(algorithmProps)
    });

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const algorithmName = watch('name');
    const algorithmId = watch('id');

    useEffect(() => {
      reset(getPlanningAlgorithmFormValues(algorithmProps));
    }, [algorithmProps, reset]);

    const isIdExist =
      algorithmId !== undefined &&
      algorithmId !== null &&
      algorithmId !== '' &&
      algorithmId !== 0;

    const onSubmit: SubmitHandler<PlanningAlgorithmFormValues> = async (
      data
    ) => {
      clearErrors('name');

      const trimmed = data.name.trim();
      if (existingAlgorithms?.length) {
        const dup = existingAlgorithms.some(
          (a) =>
            String(a.id) !== String(algorithmId) &&
            String(a.name ?? '')
              .trim()
              .toLowerCase() === trimmed.toLowerCase()
        );
        if (dup) {
          setError('name', {
            type: 'value',
            message: SAME_NAME_FIELD_MESSAGE
          });
          return;
        }
      }

      let result: ApiResponse<PlanningAlgorithm> | undefined;
      if (onUpdate && isIdExist) {
        result = await onUpdate(algorithmId, data);
      } else if (onAdd && !isIdExist) {
        result = await onAdd({ ...data, id: 0 });
      }

      if (result?.isSuccess) {
        closeDialog?.();
        return;
      }

      handleApiErrors({
        error: result?.error,
        setError,
        fieldMapping: {
          SameName: {
            field: 'name',
            message: SAME_NAME_FIELD_MESSAGE
          }
        }
      });
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => ({
      handleCancel
    }));

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      closeDialog?.();
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = async () => {
      if (onDelete && isIdExist) {
        const result = await onDelete(algorithmId);
        if (result?.isSuccess) {
          closeDialog?.();
        }
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const errorLine = (message: string | undefined, hasError: boolean) => (
      <p
        className={`operationTypes-scores-card-errors card-errors ${
          hasError ? 'text-red-500' : 'text-transparent'
        } `}
      >
        {hasError && message ? message : ' '}
      </p>
    );

    return (
      <div className="dialog-tabless-card w-full operationTypes-scores-card planningAlgorithm-card">
        <div className="flex flex-column pb-0">
          <Divider />
          <input type="hidden" {...register('id')} />
          <div
            className="scrollable-container"
            style={{
              width: '100%',
              overflow: 'auto'
            }}
          >
            <div
              className="flex flex-column scrollable-content"
              style={{ width: '99%', maxHeight: '68vh' }}
            >
              <div className="operationType-card__column">
                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="planning-algorithm-name" className="p-0">
                        Название<span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0">
                        <InputText
                          id="planning-algorithm-name"
                          className="asset-characteristic-card__input"
                          {...register('name')}
                          style={{ width: '100%' }}
                          maxLength={200}
                        />
                      </div>
                    </div>
                    {errorLine(errors.name?.message, !!errors.name)}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-start gap-3 w-full">
                      <label
                        htmlFor="planning-algorithm-desc"
                        className="p-0 pt-2"
                      >
                        Описание:
                      </label>
                      <div className="col-8 p-0">
                        <InputTextarea
                          id="planning-algorithm-desc"
                          className="asset-characteristic-card__input"
                          {...register('description')}
                          style={{ width: '100%', minHeight: '80px' }}
                          maxLength={300}
                          autoResize
                        />
                      </div>
                    </div>
                    {errorLine(
                      errors.description?.message,
                      !!errors.description
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label
                        htmlFor="validateTaskIntervalAndSchedules"
                        className="p-0"
                      >
                        Учитывать плановые сроки и фактические графики
                        сотрудников:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <Controller
                          name="validateTaskIntervalAndSchedules"
                          control={control}
                          render={({ field }) => (
                            <InputSwitch
                              inputId="validateTaskIntervalAndSchedules"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errorLine(
                      errors.validateTaskIntervalAndSchedules?.message as
                        | string
                        | undefined,
                      !!errors.validateTaskIntervalAndSchedules
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="validateAttainments" className="p-0">
                        Проводить проверку соответствия навыков и допусков:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <Controller
                          name="validateAttainments"
                          control={control}
                          render={({ field }) => (
                            <InputSwitch
                              inputId="validateAttainments"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errorLine(
                      errors.validateAttainments?.message as string | undefined,
                      !!errors.validateAttainments
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="consider-task-priority" className="p-0">
                        Учитывать приоритет наряда:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <Controller
                          name="applyTaskPriority"
                          control={control}
                          render={({ field }) => (
                            <InputSwitch
                              inputId="consider-task-priority"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errorLine(
                      errors.applyTaskPriority?.message as string | undefined,
                      !!errors.applyTaskPriority
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="interval-algorithm" className="p-0">
                        Учитывать загрузку сотрудников:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <Controller
                          name="applyEmployeeWorkload"
                          control={control}
                          render={({ field }) => (
                            <InputSwitch
                              inputId="interval-algorithm"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errorLine(
                      errors.applyEmployeeWorkload?.message as
                        | string
                        | undefined,
                      !!errors.applyEmployeeWorkload
                    )}
                  </div>
                </div>
                
                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="interval-algorithm" className="p-0">
                    Распределять наряды с учетом времени доезда:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <Controller
                          name="applyArrivalTimeOptimization"
                          control={control}
                          render={({ field }) => (
                            <InputSwitch
                              inputId="interval-algorithm"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {errorLine(
                  errors.applyArrivalTimeOptimization?.message as string | undefined,
                  !!errors.applyArrivalTimeOptimization
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="operationType-card__buttons default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
              disabled={!isIdExist}
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="алгоритм"
          entityName={algorithmName}
        />
      </div>
    );
  }
);

PlanningAlgorithmCard.displayName = 'PlanningAlgorithmCard';

export default PlanningAlgorithmCard;
